// src/invoices/xlsx.js — Excel експорти (вкл. Конто 6) и импорт на фактури (Фаза 3.2).

import { el, field, row, $ } from '../shared/dom.js';
import { round2 } from '../../services/totals.js';
import { buildKonto6Rows } from '../../services/kontoRows.js';
import { isoToBg, dateInput, localTodayIso } from '../shared/dates.js';
import { dfmt, plural } from '../shared/format.js';
import { toast } from '../shared/toast.js';
import { api } from '../shared/api.js';
import { openModal, closeModal } from '../shared/modal.js';
import { xlsxDownload, withXlsx } from '../shared/xlsx.js';
import { kontoExportDialog, recordsPreview, recordsResultDialog, WHERE } from '../shared/kontoExport.js';
import { impPick, impNum, impDigits, parseDateCell, impReadFile, statusFromLabel, importDupeNumbers } from '../shared/importers.js';
import { state, cq, activeCompanyObj, companyBooks, contacts as contactsCache } from '../shared/state.js';

// Изборът КОИ документи влизат е на сървъра (POST /invoices/konto-export): в
// същата заявка той ги и заключва. Първо ПРЕГЛЕД (ПРОМТ-11) — „N документа OK
// / M с грешки"; бутонът е неактивен, докато има грешка. Фирма на summary
// получава файла от браузъра, както досега (байт по байт); фирма на lines —
// файла, построен и запазен на сървъра (services/exportRuns.js).
export function exportKonto6(filters, contacts, allInvoices){
  // Excel библиотеката се зарежда при поискване — тук още при отварянето
  // на прозореца, за да е готова, когато фирма на summary строи файла в
  // браузъра (иначе би паднала към копието от сървъра).
  try { const p = withXlsx(() => {}); if (p && p.catch) p.catch(() => {}); } catch (e) {}
  return kontoExportDialog({
    kind: 'sales', title: 'Експорт за Конто — продажби', url: '/invoices/konto-export',
    intro: 'Фактурите по текущия филтър в списъка. След свалянето се заключват — поправка после само с отключване от администратор.',
    payload: () => ({ filters: filters || {} }),
    headline: (r) => {
      if (!r.matched) return 'Няма фактури по текущия филтър';
      const bad = new Set((r.errors || []).map((e) => e.doc)).size;
      return plural(r.ok, 'документ OK', 'документа OK') + (bad ? ' / ' + plural(bad, 'с грешка', 'с грешки') : '');
    },
    sub: (r) => (r.mode === 'records' ? 'Записвания (TXT) — CRM-ът дава сметките на статията.' : r.mode === 'lines' ? 'По редове — всеки артикул на отделен ред.' : 'По един ред на документ.'),
    where: (r) => (r.mode === 'records' ? WHERE.records : ''),
    extra: (r) => (r.mode === 'records' ? recordsPreview(r) : null),
    onApplied: (r) => {
      // Записванията: двата файла са на сървъра; след затварянето на прегледа
      // идва прозорецът със стъпките (onDone долу).
      if (r.mode === 'records') return true;
      if (r.mode === 'lines' && r.run_id) return false;
      if (!r.rows || !r.rows.length) { toast(r.message || 'Няма фактури за експорт (по текущия филтър).', true); return true; }
      if (typeof XLSX === 'undefined') return false;
      buildKonto6Xlsx(r.rows, contacts, allInvoices, r.company);
      return true;
    },
    // Документите току-що се заключиха — списъкът се преначертава, за да се
    // появят катинарите веднага, не чак при следващото влизане в екрана.
    onDone: (r) => {
      if (state().view === 'invoices') window.CRM.renderView($('#page'));
      if (r && r.mode === 'records' && r.run_id) recordsResultDialog(r);
    },
  });
}

// Редовете се строят на сървъра-споделено място services/kontoRows.js (ПРОМТ-7):
// кодовете идват от config/konto.config.json + презаписите на фирмата, а
// test/konto-export-unchanged.test.js доказва, че за фирма без презаписи
// файлът е клетка по клетка същият както преди. Тук остава само записът.
// company — кодовете от отговора на сървъра (с тях е и пазеното копие);
// без тях (стар сървър) — фирмата в паметта, както досега.
function buildKonto6Xlsx(rows, contacts, allInvoices, company){
  var r = buildKonto6Rows(rows, contacts, allInvoices, company || activeCompanyObj());
  if (!r.count) { toast('Няма подходящи фактури за Конто (липсват кодове за КИ/ДИ или 0%/освободени).', true); return; }
  var ws = XLSX.utils.aoa_to_sheet(r.aoa);
  var wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
  XLSX.writeFile(wb, 'konto6-fakturi.xlsx');
  if (r.skipped.length) toast('Готово (' + r.count + ' док.). Пропуснати без код: ' + r.skipped.join(', '), true);
  else toast('Готово: ' + r.count + ' документа за Конто 6.');
}

export function exportInvoicesDialog(invoices, contacts) {
  const from = dateInput('');
  const to = dateInput('');
  const body = el('div', {},
    el('p', { text: 'Изберете период по дата на издаване (празно = всички).', style: 'margin:0 0 10px' }),
    row(field('От дата', from), field('До дата', to))
  );
  const doExport = () => {
    const f = from.dataset.iso, t = to.dataset.iso;
    const rows = (invoices || []).filter((inv) => {
      const d = (inv.issue_date || '').slice(0, 10);
      if (f && d < f) return false;
      if (t && d > t) return false;
      return true;
    });
    if (!rows.length) { toast('Няма фактури за този период.', true); return; }
    const cName = (id) => { const c = (contacts || []).find((x) => x.id === id); return c ? c.name : ''; };
    const data = rows.sort((a, b) => (a.seq || 0) - (b.seq || 0)).map((inv) => ({
      'Номер': inv.number,
      'Дата': dfmt(inv.issue_date),
      'Падеж': dfmt(inv.due_date),
      'Клиент': cName(inv.contact_id),
      'Счетоводен шифър': (((contacts || []).find((x) => x.id === inv.contact_id)) || {}).acc_code || '',
      'ЕИК': (((contacts || []).find((x) => x.id === inv.contact_id)) || {}).eik || '',
      'Данъчна основа': Number(inv.subtotal) || 0,
      'ДДС': Number(inv.vat_amount) || 0,
      'Общо': Number(inv.total) || 0,
      'Валута': inv.currency || '',
      'Статус': (INV_STATUS[inv.status] || [inv.status])[0],
    }));
    const suffix = (f || t) ? ('-' + (f || '') + '_' + (t || '')) : '';
    xlsxDownload(data, 'Faktури', 'faktури' + suffix + '.xlsx');
    closeModal();
  };
  const foot = el('div', {},
    el('button', { class: 'btn ghost', text: 'Отказ', onClick: closeModal }),
    el('button', { class: 'btn', text: 'Свали Excel', onClick: doExport, style: 'margin-left:8px' })
  );
  openModal({ title: 'Експорт на фактури', body, foot });
}

// Изборът на фактурник при внос — през общия openModal (рецензия
// 27.10.2026, т. 4.1): ръчният прозорец нямаше Esc, заключен фокус и
// заглавие за екранния четец.
function chooseImportBook(books) {
  return new Promise((resolve) => {
    if (!books.length) { resolve({ name: 'Фактурник', start: 1 }); return; }
    if (books.length === 1) { resolve(books[0]); return; }
    let done = false;
    const finish = (v) => { if (done) return; done = true; resolve(v); };
    const sel = el('select', {}, ...books.map((b, i) => el('option', { value: String(i), text: b.name })));
    const foot = el('div', {},
      el('button', { class: 'btn ghost', type: 'button', text: 'Отказ', onClick: () => { finish(null); closeModal(); } }),
      el('button', { class: 'btn', type: 'button', text: 'Продължи', style: 'margin-left:8px', onClick: () => { finish(books[parseInt(sel.value, 10)] || books[0]); closeModal(); } }));
    openModal({ title: 'В кой фактурник да влязат фактурите?', body: el('div', {}, field('Фактурник', sel)), foot, narrow: true, onDismiss: () => finish(null) });
    sel.focus();
  });
}

export async function importInvoicesXlsx(contacts) {
  if (!state().activeCompany) { toast('Първо изберете фирма.', true); return; }
  contacts = contacts || contactsCache() || [];
  const byEik = {}, byName = {};
  contacts.forEach((c) => { if (c.eik) byEik[impDigits(c.eik)] = c; byName[(c.name || '').trim().toLowerCase()] = c; });
  const _books = companyBooks(activeCompanyObj());
  const _book = await chooseImportBook(_books);
  if (!_book) return;
  impReadFile(async (rows) => {
    const _dupes = importDupeNumbers(rows);
    if (_dupes.length) {
      alert('Във файла има ПОВТАРЯЩИ СЕ номера на фактури:\n\n' + _dupes.join(', ') + '\n\nПоправете файла и опитайте пак. НИЩО не е импортирано.');
      return;
    }
    // Двата вида пропуснати се броят поотделно (грешка от картата: всяка
    // отказана фактура се отчиташе като „липсва клиент", а причината —
    // например дублиран номер или дата преди последната — се губеше).
    let ok = 0;
    const missing = [], rejected = [];
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      const rowNo = i + 2; // ред 1 във файла е заглавният
      const eik = impDigits(impPick(r, ['Клиент ЕИК', 'ЕИК', 'eik']));
      const cname = impPick(r, ['Клиент име', 'Клиент', 'Име', 'name']);
      const contact = (eik && byEik[eik]) || (cname && byName[cname.toLowerCase()]) || null;
      if (!contact) { missing.push({ row: rowNo, who: cname || eik || '—' }); continue; }
      const payload = {
        company_id: state().activeCompany,
        contact_id: contact.id,
        book_start: _book.start,
        number: impPick(r, ['№ на документ', '№ Фактура', 'Номер', 'Фактура №', '№', 'number']),
        issue_date: parseDateCell(r['Дата'] !== '' ? r['Дата'] : r['Дата на издаване']),
        due_date: parseDateCell(r['Падеж']),
        currency: impPick(r, ['Валута', 'currency']) || 'EUR',
        vat_rate: impNum(impPick(r, ['ДДС %', 'ДДС', 'vat_rate']), 20),
        payment_method: impPick(r, ['Начин на плащане']) || 'По банков път',
        notes: impPick(r, ['Бележки', 'notes']),
        status: statusFromLabel(impPick(r, ['Статус', 'status'])),
        items: [{
          description: impPick(r, ['Описание', 'description']),
          unit: impPick(r, ['Мярка', 'Мерна единица', 'unit']),
          quantity: impNum(impPick(r, ['Количество', 'Кол.', 'qty']), 1),
          unit_price: impNum(impPick(r, ['Ед. цена', 'Единична цена', 'price']), 0),
        }],
      };
      try { await api('/invoices', { method: 'POST', body: JSON.stringify(payload) }); ok++; }
      catch (e) { rejected.push({ row: rowNo, number: payload.number, error: (e && e.message) || 'грешка' }); }
    }
    if (state().view === 'invoices') window.CRM.renderView($('#page'));
    if (!missing.length && !rejected.length) { toast('Импортирани фактури: ' + ok + '.'); return; }
    // Резултатът с пропуснатите — по ред от файла и с причината, за да се
    // поправи файлът и да се внесе отново само каквото липсва.
    const line = (t) => el('li', { text: t });
    const body = el('div', {},
      el('p', { style: 'margin:0 0 10px', text: 'Внесени: ' + ok + '. Пропуснати: ' + (missing.length + rejected.length) + '.' }),
      missing.length ? el('div', {}, el('h4', { style: 'margin:8px 0 4px', text: 'Липсва клиент в „Контакти" (' + missing.length + ')' }),
        el('ul', {}, ...missing.map((m) => line('ред ' + m.row + ': ' + m.who)))) : null,
      rejected.length ? el('div', {}, el('h4', { style: 'margin:8px 0 4px', text: 'Отказани от програмата (' + rejected.length + ')' }),
        el('ul', {}, ...rejected.map((x) => line('ред ' + x.row + (x.number ? ' (№ ' + x.number + ')' : '') + ': ' + x.error)))) : null);
    openModal({ title: 'Внос на фактури — резултат', body, foot: el('div', {}, el('button', { class: 'btn', text: 'Разбрах', onClick: closeModal })) });
  });
}

export function downloadInvoiceTemplate() {
  const data = [{
    '№ на документ': '', 'Клиент ЕИК': '123456789', 'Клиент име': 'Примерен Клиент ЕООД',
    'Дата': isoToBg(localTodayIso()), 'Падеж': '',
    'Описание': 'Консултантска услуга', 'Мярка': 'бр.', 'Количество': 1, 'Ед. цена': 100,
    'ДДС %': 20, 'Валута': 'EUR', 'Начин на плащане': 'По банков път',
    'Бележки': '', 'Статус': 'Чернова',
  }];
  xlsxDownload(data, 'Faktури', 'maket-fakturi.xlsx');
}
